import type { Agent, CaseItem } from './types';

interface AgentTeamGroup {
  teamId: string;
  teamName: string;
  agents: Agent[];
}

/**
 * Split the grid's agent rows into the rows pinned above every team (the
 * synthetic "Assigned to me" row, which belongs to no team) and one group per
 * team, in `teams` order.
 *
 * Mirrors the assignee dropdown's team grouping (`buildAssigneeGroups`): an
 * agent on several teams is listed under each of them, and a team with no
 * agent rows (e.g. the current user is its only member) is dropped. Agents keep
 * their relative order from `agents` within each group.
 */
export function groupAgentsByTeam(
  agents: Agent[],
  teams: { id?: string; name?: string }[],
): { pinned: Agent[]; groups: AgentTeamGroup[] } {
  const pinned = agents.filter(a => a.teamIds.length === 0);
  const groups = teams.flatMap(team => {
    if (!team.id) return [];
    const teamId = team.id;
    const members = agents.filter(a => a.teamIds.includes(teamId));
    if (members.length === 0) return [];
    return [{ teamId, teamName: team.name || 'Unnamed Team', agents: members }];
  });
  return { pinned, groups };
}

/**
 * Split the team groups' cases into one list per agent copy, keyed
 * `${teamId}::${agentId}`, so an agent listed under several teams shows each
 * team only that team's work. A case goes to the copy under its own `teamId`;
 * a case with no `teamId`, or whose team is not one the agent is listed under,
 * goes to the agent's first group. Every case of a grouped agent therefore
 * lands in exactly one copy. Cases of agents in no group (the pinned rows) are
 * left out.
 */
export function splitCasesByTeamGroup(
  cases: CaseItem[],
  groups: AgentTeamGroup[],
): Map<string, CaseItem[]> {
  const teamIdsByAgent = new Map<string, string[]>();
  for (const group of groups) {
    for (const agent of group.agents) {
      const teamIds = teamIdsByAgent.get(agent.id);
      if (teamIds) teamIds.push(group.teamId);
      else teamIdsByAgent.set(agent.id, [group.teamId]);
    }
  }

  const byCopy = new Map<string, CaseItem[]>();
  for (const c of cases) {
    const teamIds = teamIdsByAgent.get(c.assigneeId);
    if (!teamIds) continue;
    const teamId = teamIds.includes(c.teamId) ? c.teamId : teamIds[0];
    const key = `${teamId}::${c.assigneeId}`;
    const copyCases = byCopy.get(key);
    if (copyCases) copyCases.push(c);
    else byCopy.set(key, [c]);
  }
  return byCopy;
}
